/**
 * Property Form Component
 *
 * Handles property form functionality for the property valuation widget.
 */

import { verifySmsCode } from '../services/valuationApi';


/**
 * Initialize property form functionality
 * @param {Object} widget The widget instance
 */
export function initializePropertyForm(widget) {
  // Set up event listeners for form elements
  setupFormElements(widget);

  // Add step change event listeners
  setupStepNavigation(widget);

  // Set up move to next for verification code
  setupVerificationCodeInputs(widget);
}

/**
 * Set up event listeners for form elements
 * @param {Object} widget The widget instance
 */
function setupFormElements(widget) {
  // Property form fields
  document.querySelectorAll('[data-field]').forEach((element) => {
    const field = element.getAttribute('data-field');
    const fieldGroup = element.getAttribute('data-field-group') || 'property';

    element.addEventListener('input', (event) => {
      if (fieldGroup === 'property') {
        widget.updateFormData(field, event.target.value);
      } else if (fieldGroup === 'contact') {
        widget.updateContactData(field, event.target.value);
      } else if (fieldGroup === 'goal') {
        widget.updateGoalData(field, event.target.value);
      }
    });
  });

  // Option selection handler
  document.querySelectorAll('.option-item').forEach((option) => {
    option.addEventListener('click', () => {
      const optionGroup = option.getAttribute('data-option-group');
      const optionValue = option.getAttribute('data-value');

      widget.selectOption(
        option,
        `.option-item[data-option-group="${optionGroup}"]`
      );

      if (optionGroup === 'reason') {
        widget.updateGoalData('reason', optionValue);

        // Show pricing field
        widget.showPricingField(true);

        // Show/hide timeframe field based on reason
        const timeframeSelector = document.getElementById('timeframe-selector');
        if (timeframeSelector) {
          timeframeSelector.classList.toggle(
            'hidden-step',
            optionValue !== 'sell'
          );
        }
      }
    });
  });

  // Checkbox handlers
  document
    .querySelectorAll('input[type="checkbox"][data-field]')
    .forEach((checkbox) => {
      const field = checkbox.getAttribute('data-field');
      const fieldGroup =
        checkbox.getAttribute('data-field-group') || 'property';

      checkbox.addEventListener('change', (event) => {
        if (fieldGroup === 'property') {
          widget.updateFormData(field, event.target.checked);
        } else if (fieldGroup === 'contact') {
          widget.updateContactData(field, event.target.checked);
        }
      });
    });

  // Select handlers
  document.querySelectorAll('select[data-field]').forEach((select) => {
    const field = select.getAttribute('data-field');
    const fieldGroup = select.getAttribute('data-field-group') || 'property';

    select.addEventListener('change', (event) => {
      if (fieldGroup === 'property') {
        widget.updateFormData(field, event.target.value);
      } else if (fieldGroup === 'contact') {
        widget.updateContactData(field, event.target.value);
      } else if (fieldGroup === 'goal') {
        widget.updateGoalData(field, event.target.value);
      }
    });
  });
}

/**
 * Set up step navigation
 * @param {Object} widget The widget instance
 */
function setupStepNavigation(widget) {
  // Next buttons
  document.querySelectorAll('[data-action="next-step"]').forEach((button) => {
    const nextStep = button.getAttribute('data-next-step');

    button.addEventListener('click', () => {
      widget.showStep(parseInt(nextStep));
    });
  });

  // Back links
  document.querySelectorAll('[data-action="prev-step"]').forEach((link) => {
    const prevStep = link.getAttribute('data-prev-step');

    link.addEventListener('click', (event) => {
      event.preventDefault();
      widget.showStep(parseInt(prevStep));
    });
  });
}

/**
 * Set up verification code inputs
 * @param {Object} widget The widget instance
 */
function setupVerificationCodeInputs(widget) {
  const codeInputs = document.querySelectorAll('.code-input');

  codeInputs.forEach((input, index) => {
    input.addEventListener('keyup', function () {
      if (this.value.length >= this.maxLength) {
        const nextIndex = index + 1;

        if (nextIndex < codeInputs.length) {
          codeInputs[nextIndex].focus();
        }

        // Check if all fields are filled
        const allFilled = Array.from(codeInputs).every(
          input => input.value.length === 1
        );
        if (allFilled) {
          let code = '';
          codeInputs.forEach((input) => {
            code += input.value;
          });

          // Verify the code
          verifySmsCode(code, widget.state.formData.hash, widget.config.token)
            .then((result) => {
              if (result && result.verified) {
                widget.checkSmsCode(code);
              } else {
                alert('The code you entered is incorrect. Please try again.');
                codeInputs.forEach((input) => {
                  input.value = '';
                });
                codeInputs[0].focus();
              }
            })
            .catch((error) => {
              console.error('Error verifying SMS code:', error);
              alert(
                'There was an error verifying your code. Please try again.'
              );
            });
        }
      }
    });

    // Allow backspace to go to previous input
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Backspace' && !this.value && index > 0) {
        codeInputs[index - 1].focus();
      }
    });
  });
}
